import type { KeyboardEvent, PointerEvent } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

const STEP = 16;
const STEP_LARGE = 64;

/**
 * Vertical divider between two panes. The library has no splitter, so this stays
 * in the app (WAI-ARIA "window splitter"): drag with the pointer, or focus it and
 * use the arrow keys (Shift for a larger step), Home and End.
 *
 * The keyboard needs the current width and its limits, so pass `value`, `min`,
 * `max` and `onValueChange` together (`useResizablePanelWidth().resizeProps`).
 * Without them the handle is pointer-only, as before.
 */
export function ResizeHandle({
  onPointerDown,
  className,
  value,
  min,
  max,
  onValueChange,
  label,
}: {
  onPointerDown: (e: PointerEvent) => void;
  className?: string;
  /** Current width of the pane before the handle, in px. */
  value?: number;
  min?: number;
  max?: number;
  onValueChange?: (width: number) => void;
  /** Accessible name; defaults to the shared "Resize panel". */
  label?: string;
}) {
  const { t } = useTranslation("single-app");
  const keyboard =
    value !== undefined && min !== undefined && max !== undefined && onValueChange !== undefined;

  const onKeyDown = (e: KeyboardEvent) => {
    if (!keyboard) return;
    const step = e.shiftKey ? STEP_LARGE : STEP;
    const next =
      e.key === "ArrowLeft"
        ? value - step
        : e.key === "ArrowRight"
          ? value + step
          : e.key === "Home"
            ? min
            : e.key === "End"
              ? max
              : null;
    if (next === null) return;
    e.preventDefault();
    onValueChange(Math.min(max, Math.max(min, next)));
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={keyboard ? (label ?? t("editor.resizePanel")) : undefined}
      aria-valuenow={keyboard ? value : undefined}
      aria-valuemin={keyboard ? min : undefined}
      aria-valuemax={keyboard ? max : undefined}
      tabIndex={keyboard ? 0 : undefined}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      className={cn(
        "group relative w-1 shrink-0 cursor-col-resize touch-none select-none outline-none",
        className,
      )}
    >
      <div className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-hairline group-hover:bg-brand group-active:bg-brand group-focus-visible:w-0.5 group-focus-visible:bg-brand" />
    </div>
  );
}
